import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Textarea, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, IconButton, Divider, Select, Table, Thead, Tbody, Tr, Th, Td, Tabs, TabList, Tab, TabPanels, TabPanel,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiPackage } from 'react-icons/fi';
import api from '../api';

export default function ResourcesModal({ isOpen, onClose }) {
  const [resources, setResources] = useState([]);
  const [types, setTypes] = useState([]);
  const [bookingTypes, setBookingTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState({ resource_code: '', name: '', description: '', resource_type_id: '', hourly_cost: '', daily_cost: '', target_profit_per_unit: '' });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newType, setNewType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const toast = useToast();

  const fetchResources = async () => {
    try {
      let url = '/resources?';
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (typeFilter) url += `type_id=${typeFilter}&`;
      const { data } = await api.get(url);
      setResources(data);
    } catch(e) {}
  };
  const fetchTypes = async () => { try { const { data } = await api.get('/resources/types'); setTypes(data); } catch(e) {} };
  const fetchBookingTypes = async () => { try { const { data } = await api.get('/resources/booking-types'); setBookingTypes(data); } catch(e) {} };
  const fetchDepts = async () => { try { const { data } = await api.get('/departments'); setDepartments(data); } catch(e) {} };
  const fetchBookings = async (id) => {
    try {
      let url = `/resources/${id}/bookings?`;
      if (dateFrom) url += `from=${dateFrom}&`;
      if (dateTo) url += `to=${dateTo}&`;
      const { data } = await api.get(url);
      setBookings(data);
    } catch(e) { setBookings([]); }
  };

  useEffect(() => { if (isOpen) { fetchResources(); fetchTypes(); fetchBookingTypes(); fetchDepts(); setSelected(null); setShowForm(false); } }, [isOpen]);
  useEffect(() => { if (isOpen) fetchResources(); }, [search, typeFilter]);

  const handleSave = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      const payload = { ...form, resource_type_id: form.resource_type_id || null, hourly_cost: form.hourly_cost || null, daily_cost: form.daily_cost || null, target_profit_per_unit: form.target_profit_per_unit || null };
      if (editingId) await api.put(`/resources/${editingId}`, payload);
      else await api.post('/resources', payload);
      await fetchResources(); setShowForm(false); setEditingId(null);
      setForm({ resource_code: '', name: '', description: '', resource_type_id: '', hourly_cost: '', daily_cost: '', target_profit_per_unit: '' });
      toast({ title: editingId ? 'Updated' : 'Created', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const openEdit = (r) => {
    setForm({ resource_code: r.resource_code || '', name: r.name, description: r.description || '', resource_type_id: r.resource_type_id || '', hourly_cost: r.hourly_cost || '', daily_cost: r.daily_cost || '', target_profit_per_unit: r.target_profit_per_unit || '' });
    setEditingId(r.id); setShowForm(true);
  };

  const selectResource = (r) => { setSelected(r); fetchBookings(r.id); };

  const fmtDate = (d) => d ? new Date(d.split('T')[0] + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex justify="space-between" pr={8}>
            <HStack><FiPackage /><Text>Resources</Text></HStack>
            <HStack spacing={1}>
              <Button size="xs" colorScheme="purple" variant="outline"
                onClick={async () => { try { const { data } = await api.post('/resources/sync-people'); toast({ title: data.message, status: 'success', duration: 2000 }); fetchResources(); } catch(e) {} }}>
                Sync People
              </Button>
              <Button size="xs" leftIcon={<FiPlus />} colorScheme="teal" variant={showForm ? 'solid' : 'outline'}
                onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ resource_code: '', name: '', description: '', resource_type_id: '', hourly_cost: '', daily_cost: '', target_profit_per_unit: '' }); }}>
                {showForm ? 'Cancel' : 'New Resource'}
              </Button>
            </HStack>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          <Tabs size="sm" colorScheme="teal">
            <TabList><Tab fontSize="12px">Resources</Tab><Tab fontSize="12px">Resource Types</Tab><Tab fontSize="12px">Booking Types</Tab></TabList>
            <TabPanels>
              {/* Resources Tab */}
              <TabPanel px={0}>
                {showForm && (
                  <Box bg="teal.50" p={3} borderRadius="md" mb={4}>
                    <VStack spacing={2}>
                      <HStack w="100%" spacing={2}>
                        <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Code (optional)</FormLabel>
                          <Input size="sm" value={form.resource_code} onChange={e => setForm(p => ({ ...p, resource_code: e.target.value }))} placeholder="e.g. ROOM-01" /></FormControl>
                        <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Name</FormLabel>
                          <Input size="sm" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></FormControl>
                        <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Type</FormLabel>
                          <Select size="sm" value={form.resource_type_id} onChange={e => setForm(p => ({ ...p, resource_type_id: e.target.value }))}>
                            <option value="">None</option>
                            {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </Select></FormControl>
                      </HStack>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Description</FormLabel>
                        <Textarea size="sm" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></FormControl>
                      <HStack w="100%" spacing={2}>
                        <FormControl><FormLabel fontSize="xs" mb={0.5}>Hourly Cost</FormLabel>
                          <Input size="sm" type="number" step="0.01" value={form.hourly_cost} onChange={e => setForm(p => ({ ...p, hourly_cost: e.target.value }))} /></FormControl>
                        <FormControl><FormLabel fontSize="xs" mb={0.5}>Daily Cost</FormLabel>
                          <Input size="sm" type="number" step="0.01" value={form.daily_cost} onChange={e => setForm(p => ({ ...p, daily_cost: e.target.value }))} /></FormControl>
                        <FormControl><FormLabel fontSize="xs" mb={0.5}>Target Profit/Unit</FormLabel>
                          <Input size="sm" type="number" step="0.01" value={form.target_profit_per_unit} onChange={e => setForm(p => ({ ...p, target_profit_per_unit: e.target.value }))} /></FormControl>
                      </HStack>
                      <Button size="xs" colorScheme="teal" onClick={handleSave} isLoading={saving} alignSelf="flex-end">{editingId ? 'Update' : 'Create'}</Button>
                    </VStack>
                  </Box>
                )}

                <HStack mb={3} spacing={2}>
                  <Input size="sm" placeholder="Search resources..." value={search} onChange={e => setSearch(e.target.value)} flex={2} />
                  <Select size="sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} flex={1}>
                    <option value="">All Types</option>
                    {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </Select>
                </HStack>

                <HStack spacing={4} align="start">
                  <VStack spacing={1} align="stretch" flex={1} minW="250px" maxH="400px" overflowY="auto">
                    {resources.map(r => (
                      <Box key={r.id} p={2} border="1px" borderColor={selected?.id === r.id ? 'teal.400' : 'gray.100'}
                        borderRadius="md" cursor="pointer" bg={selected?.id === r.id ? 'teal.50' : 'white'}
                        _hover={{ bg: 'gray.50' }} onClick={() => selectResource(r)}>
                        <Flex justify="space-between" align="center">
                          <Box>
                            <Flex align="center" gap={2}>
                              {r.resource_code && <Badge fontSize="8px" colorScheme="gray">{r.resource_code}</Badge>}
                              <Text fontSize="12px" fontWeight="600">{r.name}</Text>
                              {r.type_name && <Badge fontSize="8px" colorScheme={r.is_person ? 'purple' : 'teal'}>{r.type_name}</Badge>}
                              {r.is_person && <Badge fontSize="7px" colorScheme="purple" variant="subtle">👤</Badge>}
                            </Flex>
                            {r.description && <Text fontSize="10px" color="gray.500" noOfLines={1}>{r.description}</Text>}
                          </Box>
                          <HStack spacing={1}>
                            <IconButton icon={<FiEdit2 />} size="xs" variant="ghost" onClick={(e) => { e.stopPropagation(); openEdit(r); }} aria-label="Edit" />
                            <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                              onClick={async (e) => { e.stopPropagation(); await api.delete(`/resources/${r.id}`); fetchResources(); if (selected?.id === r.id) setSelected(null); }} aria-label="Delete" />
                          </HStack>
                        </Flex>
                        <HStack mt={1} spacing={2} fontSize="10px" color="gray.500">
                          {r.hourly_cost && <Text>£{r.hourly_cost}/hr</Text>}
                          {r.daily_cost && <Text>£{r.daily_cost}/day</Text>}
                        </HStack>
                      </Box>
                    ))}
                    {resources.length === 0 && <Text fontSize="11px" color="gray.400" textAlign="center" py={4}>No resources found</Text>}
                  </VStack>

                  {selected && (
                    <Box flex={1} minW="280px" p={3} border="1px" borderColor="gray.200" borderRadius="md">
                      <Text fontSize="14px" fontWeight="700" mb={1}>{selected.name}</Text>
                      {selected.resource_code && <Badge fontSize="9px" colorScheme="gray" mb={1}>{selected.resource_code}</Badge>}
                      {selected.description && <Text fontSize="11px" color="gray.500" mb={2}>{selected.description}</Text>}
                      <Divider mb={2} />
                      <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" mb={1}>Calendar Bookings</Text>
                      <HStack mb={2} spacing={1}>
                        <Input size="xs" type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); }} placeholder="From" />
                        <Input size="xs" type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); }} placeholder="To" />
                        <Button size="xs" colorScheme="teal" onClick={() => fetchBookings(selected.id)}>Filter</Button>
                      </HStack>
                      {bookings.length > 0 ? (
                        <Table size="sm"><Thead><Tr><Th px={1}>Date</Th><Th px={1}>Title</Th><Th px={1}>Task</Th><Th px={1}>Project</Th></Tr></Thead>
                          <Tbody>{bookings.map(b => (
                            <Tr key={b.id} _hover={{ bg: 'gray.50' }}>
                              <Td px={1} fontSize="10px" whiteSpace="nowrap">{fmtDate(b.start_date)}</Td>
                              <Td px={1} fontSize="10px">{b.title}</Td>
                              <Td px={1} fontSize="10px">{b.task_title || '—'}</Td>
                              <Td px={1} fontSize="10px">{b.project_name || '—'}</Td>
                            </Tr>
                          ))}</Tbody>
                        </Table>
                      ) : <Text fontSize="10px" color="gray.400">No bookings</Text>}
                    </Box>
                  )}
                </HStack>
              </TabPanel>

              {/* Resource Types Tab */}
              <TabPanel px={0}>
                <HStack mb={2}>
                  <Input size="sm" placeholder="New type name..." value={newType} onChange={e => setNewType(e.target.value)} />
                  <Button size="sm" colorScheme="teal" onClick={async () => { if (!newType) return; await api.post('/resources/types', { name: newType }); setNewType(''); fetchTypes(); }}>Add</Button>
                </HStack>
                <VStack spacing={1} align="stretch">
                  {types.map(t => (
                    <Flex key={t.id} p={2} border="1px" borderColor="gray.100" borderRadius="md" justify="space-between" align="center">
                      <Text fontSize="12px" fontWeight="500">{t.name}</Text>
                      <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                        onClick={async () => { await api.delete(`/resources/types/${t.id}`); fetchTypes(); }} aria-label="Delete" />
                    </Flex>
                  ))}
                </VStack>
              </TabPanel>

              {/* Booking Types Tab */}
              <TabPanel px={0}>
                <VStack spacing={1} align="stretch">
                  {bookingTypes.map(bt => (
                    <Flex key={bt.id} p={2} border="1px" borderColor="gray.100" borderRadius="md" justify="space-between" align="center">
                      <HStack>
                        <Text fontSize="12px" fontWeight="500">{bt.name}</Text>
                        {bt.is_internal && <Badge fontSize="8px" colorScheme="orange">Internal</Badge>}
                      </HStack>
                      <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                        onClick={async () => { await api.delete(`/resources/booking-types/${bt.id}`); fetchBookingTypes(); }} aria-label="Delete" />
                    </Flex>
                  ))}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}
